import React, { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import "./Subscription.scss";
import ControlPanelHeader from "../../components/ControlPanelHeader";
import SearchFieldControl from "../../components/SearchFieldControl";
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { planList } from "../../../services/PlanService";
import { ArrowForwardIosSharp } from "@material-ui/icons";
import Loader from "../../../components/Loader";
import { useCallback } from "react";
import { debounce } from "lodash";
import OverflowTooltip from "./OverflowTooltip";
import {
  archivePlan,
  checkControlPanelUser,
} from "../../../services/Subscription";
import CommonUtil from "../../../util/CommonUtils";
import { ReactComponent as ArchiveIcon } from "../../../assets/images/archive-icon.svg";
import { toast } from "sonner";
import CancelDialog from "../../components/CancelDialog";

export default function Subscription() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isXlScreen = useMediaQuery(theme.breakpoints.up("xl"));
  const [isLoading, setIsLoading] = useState(false);
  const [clearSearch, setClearSearch] = useState(false);
  const [rows, setRows] = useState([]);
  const { setCreatePlanPageStepCount } = useOutletContext();

  const [planToArchive, setPlanToArchive] = useState("");

  const handleCreatePlan = () => {
    navigate("/plan/new");
  };

  const [filter, setFilter] = useState({
    name: "",
    planOrder: "",
  });

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("USER"));
    checkControlPanelUser(storedUser.email).then((res) => {
      if (res.data?.code === "RCI0000") {
        loadPlans();
        localStorage.removeItem("plan_name");
      } else {
        navigate("/dashboard");
      }
    });
    setCreatePlanPageStepCount(0);
    loadPlans();
  }, [filter]);

  const loadPlans = () => {
    setIsLoading(true);
    const name = filter?.name.replace(/^\s+/, "") || "";
    const planOrder = filter?.planOrder || "";
    const intervalUnit = "MONTHS";

    var requestData = {
      name,
      planOrder,
      intervalUnit,
    };

    planList(requestData).then((res) => {
      if (res?.status === 200 && res?.data?.code === "RCI0000") {
        const sortedPlans = res.data.data.plans.sort((a, b) => {
          return parseInt(b.planOrder) - parseInt(a.planOrder);
        });
        setRows(sortedPlans);
      } else {
        setRows([]);
      }
      setIsLoading(false);
    });
  };

  const handleArchive = (planOrder) => {
    archivePlan(planOrder).then((res) => {
      if (res.data.code === "RCI0050") {
        toast.success(t("RAL0964"));
        loadPlans();
      } else {
        toast.error(t("RAL0965"));
      }
    });
  };

  const cardTile = (plan) => {
    return (
      <Box className="tile">
        <Paper className="cardtile" elevation={0}>
          <Grid container display={"flex"} alignItems="center">
            <Grid item xs={2}>
              <Tooltip>
                <Typography className="cardtile-typography">
                  {plan.planOrder}
                </Typography>
              </Tooltip>
            </Grid>
            <Grid item xs={8}>
              <OverflowTooltip text={plan.name} />
            </Grid>
            <Grid
              item
              xs={2}
              container
              justifyContent="flex-end"
              display="flex"
            >
              <Tooltip title={t("RAL0963")}>
                <IconButton
                  onClick={() => setPlanToArchive(plan.planOrder)}
                  disabled={plan?.trialPlan}
                >
                  <ArchiveIcon
                    id="plan-archive-icon"
                    className="archive_svg"
                    // height={isXlScreen ? "18px" : "14px"}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("RAL0778")}>
                <IconButton
                  onClick={() => {
                    localStorage.setItem("plan_name", plan.name);
                    navigate(`/plan/view/${plan.planOrder}`);
                  }}
                >
                  <ArrowForwardIosSharp className="iconcolorblack" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    );
  };

  const handleSearchChange = useCallback(
    debounce((newValue) => {
      setFilter({ ...filter, name: newValue });
    }, 200),
    []
  );

  return (
    <div>
      <SearchFieldControl
        // label={t('RAL0781')}
        label={"Search with plan name"}
        value={filter.name}
        maxLength={50}
        onClick={(value) => {
          setClearSearch(false);
          setFilter({ ...filter, name: value });
        }}
        onChange={(newValue) => {
          if (newValue.trim() !== "") {
            handleSearchChange(newValue);
          } else {
            setFilter({ ...filter, name: "" });
          }
        }}
        onClear={() => {
          setFilter({ ...filter, name: "" });
        }}
      />
      {isLoading && <Loader open={isLoading} />}

      {!isLoading && rows.length > 0 ? (
        <>
          <div>
            <Box className="tile-header">
              <Paper className="cardtile-header" elevation={0}>
                <Grid container display={"flex"}>
                  <Grid xs={2}>
                    <Tooltip>
                      <Typography className="header-typography">
                        {/* {t('RAL0776')} */}
                        Sequence ID
                      </Typography>
                    </Tooltip>
                  </Grid>
                  <Grid xs={8}>
                    <Tooltip>
                      <Typography className="header-typography">
                        {/* {t('RAL0777')} */}
                        Plan
                      </Typography>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Paper>
            </Box>

            <div className="scrollable-container">
              {rows.map((item, index) => {
                return cardTile(item);
              })}
            </div>
          </div>
        </>
      ) : (
        <Box
          sx={{
            display: rows.length > 0 ? "none" : "block",
          }}
        >
          <div className="leftthings">
            <p className="EmptyName">
              {filter.name ? t("RAL0780") : t("RAL0779")}
            </p>
          </div>
        </Box>
      )}

      {!CommonUtil.isEmptyString(planToArchive) && (
        <CancelDialog
          open={!CommonUtil.isEmptyString(planToArchive)}
          onClose={() => {
            setPlanToArchive("");
          }}
          onConfirm={() => {
            handleArchive(planToArchive);
            setPlanToArchive("");
          }}
          title={"Archive plan"}
          content={"Are you sure you want to archive this plan?"}
        />
      )}
    </div>
  );
}
